import escape from 'css.escape';

// TODO: change all this enums to bitflags with lookup at serialize?
export type PseudoType =
  | 'active'
  | 'checked'
  | 'disabled'
  | 'focus'
  | 'focus-visible'
  | 'focus-within'
  | 'hover'
  | 'visited'
  | 'lang'
  | 'empty'
  | 'popover-open';
export const Pseudo = Object.freeze({
  Active: 'active',
  Checked: 'checked',
  Disabled: 'disabled',
  Focus: 'focus',
  FocusVisible: 'focus-visible',
  FocusWithin: 'focus-within',
  Hover: 'hover',
  Visited: 'visited',
  Empty: 'empty',
  Lang: 'lang',
  PopoverOpen: 'popover-open',
});

export type PseudoElementType = 'after' | 'before' | 'placeholder' | 'backdrop';
export const PseudoElement: {
  After: 'after';
  Before: 'before';
  Placeholder: 'placeholder';
  Backdrop: 'backdrop';
} = Object.freeze({
  After: 'after',
  Before: 'before',
  Placeholder: 'placeholder',
  Backdrop: 'backdrop',
});

export type PseudoFunctionType = 'where';
export const PseudoFunction: {
  Where: 'where';
} = Object.freeze({
  Where: 'where',
});

export type CombinatorType = '>' | ' ' | '+';
export const Combinator: {
  Child: '>';
  Descendent: ' ';
  NextSibling: '+';
} = Object.freeze({
  Child: '>',
  Descendent: ' ',
  NextSibling: '+',
});

export type CustomNodeStateType =
  | 'open'
  | 'current' // FormCheckbox / FormRadioInput default conditional style hack
  | 'redirected-checked' // FormCheckbox / FormRadioInput default conditional style hack
  | 'redirected-focus' // FormCheckbox / FormRadioInput default conditional style hack
  | 'redirected-focus-visible'
  | 'ecommerce-pill-selected'
  | 'ecommerce-pill-disabled'
  | 'ecommerce-add-to-cart-disabled'
  | 'ecommerce-buy-now-disabled';

export const CustomNodeState: {
  Open: 'open';
  Current: 'current';
  RedirectedFocus: 'redirected-focus';
  RedirectedFocusVisible: 'redirected-focus-visible';
  RedirectedChecked: 'redirected-checked';
  EcommercePillSelected: 'ecommerce-pill-selected';
  EcommercePillDisabled: 'ecommerce-pill-disabled';
  EcommerceAddToCartDisabled: 'ecommerce-add-to-cart-disabled';
  EcommerceBuyNowDisabled: 'ecommerce-buy-now-disabled';
} = Object.freeze({
  Open: 'open',
  Current: 'current',
  RedirectedFocus: 'redirected-focus',
  RedirectedFocusVisible: 'redirected-focus-visible',
  RedirectedChecked: 'redirected-checked',
  EcommercePillSelected: 'ecommerce-pill-selected',
  EcommercePillDisabled: 'ecommerce-pill-disabled',
  EcommerceAddToCartDisabled: 'ecommerce-add-to-cart-disabled',
  EcommerceBuyNowDisabled: 'ecommerce-buy-now-disabled',
});

type ComponentType = //  LOCAL_NAME
  | 0 //  LOCAL_NAME
  | 1 //  ID
  | 2 //  CLASS
  | 3 //  PSEUDO
  | 4 //  COMBINATOR
  | 5 //  CUSTOM_NODE_STATE
  | 6 //  PSEUDO_ELEMENT
  | 7; // PSEUDO_FUNCTION
export class Component {
  type: ComponentType;
  value: string;
  arg: Component | undefined;

  static LOCAL_NAME: ComponentType = 0;
  static ID: ComponentType = 1;
  static CLASS: ComponentType = 2;
  static PSEUDO: ComponentType = 3;
  static COMBINATOR: ComponentType = 4;
  static CUSTOM_NODE_STATE: ComponentType = 5;
  static PSEUDO_ELEMENT: ComponentType = 6;
  static PSEUDO_FUNCTION: ComponentType = 7;

  static LocalName(name: string) {
    return new Component(Component.LOCAL_NAME, escape(name));
  }
  static Combinator(comb: CombinatorType) {
    return new Component(Component.COMBINATOR, comb);
  }
  static Class(className: string) {
    return new Component(Component.CLASS, escape(className));
  }
  static Id(id: string) {
    return new Component(Component.ID, escape(id));
  }
  static Pseudo(pseudo: PseudoType) {
    return new Component(Component.PSEUDO, pseudo);
  }
  static PseudoElement(element: PseudoElementType) {
    return new Component(Component.PSEUDO_ELEMENT, element);
  }
  static PseudoFunction(func: PseudoFunctionType, arg: Component) {
    return new Component(Component.PSEUDO_FUNCTION, func, arg);
  }
  static CustomNodeState(state: CustomNodeStateType) {
    return new Component(Component.CUSTOM_NODE_STATE, state);
  }

  static createFromVariantKey(
    variantKey: PseudoType | PseudoElementType | CustomNodeStateType
  ): Component | null {
    switch (variantKey) {
      case PseudoElement.After:
      case PseudoElement.Before:
      case PseudoElement.Placeholder:
      case PseudoElement.Backdrop:
        return Component.PseudoElement(variantKey as any);
      case Pseudo.Active:
      case Pseudo.Checked:
      case Pseudo.Empty:
      case Pseudo.Focus:
      case Pseudo.FocusVisible:
      case Pseudo.FocusWithin:
      case Pseudo.Hover:
      case Pseudo.Visited:
      case Pseudo.Lang:
      case Pseudo.PopoverOpen:
        return Component.Pseudo(variantKey as any);
      case CustomNodeState.Open:
      case CustomNodeState.Current:
      case CustomNodeState.RedirectedFocus:
      case CustomNodeState.RedirectedChecked:
      case CustomNodeState.EcommercePillSelected:
      case CustomNodeState.EcommercePillDisabled:
      case CustomNodeState.EcommerceAddToCartDisabled:
      case CustomNodeState.EcommerceBuyNowDisabled:
        return Component.CustomNodeState(variantKey as any);
      default:
        return null;
    }
  }

  constructor(type: ComponentType, value: string, arg?: Component) {
    this.type = type;
    this.value = value;
    this.arg = arg;
  }

  toString(): string {
    switch (this.type) {
      case Component.LOCAL_NAME:
        return this.value;
      case Component.ID:
        return `#${this.value}`;
      case Component.CLASS:
        return `.${this.value}`;
      case Component.PSEUDO:
        return `:${this.value}`;
      case Component.PSEUDO_ELEMENT:
        return `::${this.value}`;
      case Component.PSEUDO_FUNCTION:
        return `:${this.value}(${this.arg?.toString()})`;
      case Component.COMBINATOR:
        switch (this.value) {
          case Combinator.Descendent:
            return ' ';
          case Combinator.NextSibling:
            return ' + ';
          case Combinator.Child:
            return ' > ';
          default:
            return ` ${this.value} `;
        }
      case Component.CUSTOM_NODE_STATE:
        // We serialize out custom node state types as `w--` prefixed strings.
        return `.w--${this.value}`;
      default:
        return this.value;
    }
  }

  toJSON() {
    switch (this.type) {
      case Component.LOCAL_NAME:
        return {type: 'LOCAL_NAME', value: this.value};
      case Component.ID:
        return {type: 'ID', value: this.value};
      case Component.CLASS:
        return {type: 'CLASS', value: this.value};
      case Component.PSEUDO:
        return {type: 'PSEUDO', value: this.value};
      case Component.COMBINATOR:
        return {type: 'COMBINATOR', value: this.value};
      case Component.CUSTOM_NODE_STATE:
        return {type: 'CUSTOM_NODE_STATE', value: this.value};
      default:
        return this;
    }
  }
}

export class Selector {
  specificity: number;
  components: Array<Component>;
  /*:: @@iterator: () => Iterator<Component>; */

  // TODO: Implement matching order storage (right -> left) chunking on combinators.
  // We create Selector instances by passing Components in "parse order" (left -> right).
  // For matching performance we want to store these components in "matching order" (right -> left).
  constructor(components: Array<Component>) {
    this.components = components;
    this.specificity = specificity(components);
  }

  [Symbol.iterator](): Iterator<Component> {
    return new MatchingOrderIter(this.components);
  }

  toString() {
    let str = '';
    for (const component of this.components) {
      str += component.toString();
    }
    return str;
  }
}

class MatchingOrderIter {
  index: number;
  components: Array<Component>;
  /*:: @@iterator: () => Iterator<Component>; */

  constructor(components: Array<Component>) {
    this.index = components.length;
    this.components = components;
  }

  [Symbol.iterator]() {
    return this;
  }

  next(): IteratorResult<Component, null> {
    if (--this.index >= 0) {
      const current = this.components[this.index];
      // @ts-expect-error - TS2322 - Type 'Component | undefined' is not assignable to type 'Component | null'.
      return {value: current, done: false};
    }
    return {value: null, done: true};
  }
}

export class SelectorList {
  selectors: Array<Selector>;
  constructor(selectors: Array<Selector>) {
    this.selectors = selectors;
  }
}

// https://www.w3.org/TR/selectors-4/#specificity-rules
// prettier-ignore
function specificity(components: Array<Component>): number {
  let a = 0;
  let b = 0;
  let c = 0;
  for (const component of components) {
    switch (component.type) {
      case Component.ID: a++; break;
      case Component.CLASS:
      case Component.PSEUDO: b++; break;
      case Component.LOCAL_NAME: c++; break;
      default: continue;
    }
  }
  return (a << 20) | (b << 10) | c;
}
